<template>
	<div class="base-table">
		<table>
			<thead>
				<tr>
					<th
						v-for="(column, colIndex) in visibleColumns"
						:key="colIndex"
						:style="getColumnStyle(column)"
					>
						{{ column.label }}
					</th>
					<th v-if="hasRowOptions"></th>
				</tr>
			</thead>

			<tbody>
				<tr v-if="isEmpty" key="empty" class="base-table__empty-row">
					<td class="base-table__empty" :colspan="columnsCount">
						<slot name="empty">No entries to show</slot>
					</td>
				</tr>

				<template v-else-if="isLoading">
					<tr v-for="i in loadingDummiesCount" :key="i" class="base-table__loading-row">
						<td class="base-table__loading" :colspan="columnsCount">
							<slot name="loading">Loading...</slot>
						</td>
					</tr>
				</template>

				<tr
					v-for="(row, rowIndex) in data"
					v-else
					:key="rowIndex"
					:class="getRowClass(rowIndex, row)"
				>
					<slot name="row" v-bind="{ row }">
						<td
							v-for="(column, colIndex) in visibleColumns"
							:key="colIndex"
							:class="[
								{
									'base-table__td--primary': !!column.primary,
								},
								...getColumnClass(column.class),
							]"
						>
							<slot :name="`col-${column.field}`" v-bind="{ row, item: row[column.field] }">
								{{ row[column.field] }}
							</slot>
						</td>
					</slot>

					<td v-if="hasRowOptions">
						<slot name="row-options" v-bind="{ row }" />
					</td>
				</tr>
			</tbody>

			<tfoot v-if="hasFooter">
				<tr>
					<td :colspan="columnsCount">
						<slot name="footer" />
					</td>
				</tr>
			</tfoot>
		</table>
	</div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';

import { useHasSlot } from '@/composables';
import { AnyObject, VueClassBinding } from '@/tools/types';

import { Column } from './types';

export type GetRowClass<R extends AnyObject = AnyObject> = (
	index: number,
	row: R,
) => VueClassBinding;

export default defineComponent({
	provide() {
		const tableInstance = this as any;

		return {
			$table: tableInstance,
		};
	},

	props: {
		isLoading: {
			type: Boolean,
			default: false,
		},

		loadingCount: {
			type: Number,
			default: 1,
		},

		data: {
			type: Array as PropType<AnyObject[]>,
			default: () => [],
		},

		columns: {
			type: Array as PropType<Column[]>,
			default: () => [],
		},

		rowClass: {
			type: Function as any as PropType<GetRowClass | null>,
			default: null,
		},
	},

	setup(props, { slots }) {
		return {
			hasRowOptions: useHasSlot('row-options', slots),
			hasFooter: useHasSlot('footer', slots),
		};
	},

	data() {
		return {
			customColumns: [] as Array<Column>,
		};
	},

	computed: {
		visibleColumns(): Array<Column> {
			return this.customColumns.length ? this.customColumns : this.columns;
		},

		columnsCount(): number {
			return this.visibleColumns.length + (this.hasRowOptions ? 1 : 0);
		},

		isEmpty(): boolean {
			return (!this.data || !(this.data as []).length) && !this.isLoading;
		},

		loadingDummiesCount(): number {
			return this.data?.length || this.loadingCount;
		},
	},

	methods: {
		registerColumn(col: Column): void {
			const existingIndex = this.customColumns.findIndex(c => c.field === col.field);

			if (existingIndex >= 0) {
				this.customColumns[existingIndex] = col;
			} else {
				this.customColumns.push(col);
			}
		},

		getColumnClass(classes?: string[]) {
			return Array.isArray(classes) ? classes : [];
		},

		getRowClass(index: number, row: AnyObject) {
			if (this.rowClass) {
				return this.rowClass(index, row);
			}
		},

		getColumnStyle({ width }: Column): AnyObject | undefined {
			if (!width) {
				return;
			}

			return {
				width,
			};
		},
	},
});
</script>

<style lang="scss">
.base-table {
	table {
		width: 100%;

		thead {
			tr {
				border-bottom: 1px solid #eaeaea;
			}

			th {
				padding: 1rem;
				color: #666;
				font-size: 1.2rem;
				font-weight: 500;
				text-align: left;
			}
		}

		tbody {
			tr {
				border-bottom: 1px solid #eaeaea;
			}

			td {
				padding: 1rem;
				font-size: 0.9em;
				vertical-align: middle;

				&.base-table__td--primary {
					font-size: 1em;
					font-weight: 500;
				}
			}

			&:last-child {
				tr {
					&:last-child {
						border-bottom: none;
					}
				}
			}
		}

		tfoot {
			td {
				padding: 2rem 0 1rem;
				text-align: left;
			}
		}

		td,
		th {
			&:first-child {
				padding-left: 0;
			}

			&:last-child {
				padding-right: 0;

				&:not(:first-child) {
					text-align: right;
				}
			}
		}
	}
}
</style>
